import { z } from 'zod';
import { DisplayField, Sex, TitlePrefix } from '@mews-commander/guest-profile';
import { Validation } from '@mews/framework';
import { Optional } from '@mews/std';
import { getKeyByDisplayField } from './components';

const fieldSchema = {
    [DisplayField.AccountingCode]: Validation.String.Required.nullable(),
    [DisplayField.BillingCode]: Validation.String.Required.nullable(),
    [DisplayField.BirthDate]: Validation.String.Required.nullable(),
    [DisplayField.BirthPlace]: Validation.String.Required.nullable(),
    [DisplayField.CarRegistrationNumber]: Validation.String.Required.nullable(),
    [DisplayField.DietaryRequirements]: Validation.String.Required.nullable(),
    [DisplayField.Email]: Validation.String.Required.nullable(),
    [DisplayField.FirstName]: Validation.String.Required.nullable(),
    [DisplayField.LastName]: Validation.String.Required,
    [DisplayField.LoyaltyCode]: Validation.String.Required.nullable(),
    [DisplayField.LoyaltyMembership]: Validation.String.Required.nullable(),
    [DisplayField.NationalityCode]: Validation.String.Required.nullable(),
    [DisplayField.Occupation]: Validation.String.Required.nullable(),
    [DisplayField.Phone]: Validation.String.Required.nullable(),
    [DisplayField.PreferredLanguageCode]: Validation.String.Required.nullable(),
    [DisplayField.SecondLastName]: Validation.String.Required.nullable(),
    [DisplayField.Sex]: z.nativeEnum(Sex).nullable(),
    [DisplayField.TaxIdentifier]: Validation.String.Required.nullable(),
    [DisplayField.TitlePrefix]: z.nativeEnum(TitlePrefix).nullable(),
};

type FieldSchema = keyof typeof fieldSchema;
interface FieldSchemaValue {
    value: (typeof fieldSchema)[FieldSchema] | Optional<string>;
}

export const createZodSchema = (preSelectedFieldsNames: Array<DisplayField>) => {
    const ignoreIfMatchedField = (field: DisplayField) =>
        z
            .union([fieldSchema[field], z.undefined()])
            .refine((value) => !preSelectedFieldsNames.includes(field) || value !== undefined);

    const createFieldSchema = (field: DisplayField) =>
        z.object({
            value: fieldSchema[field]?.or(ignoreIfMatchedField(field)),
        });

    const schemaObject = preSelectedFieldsNames.reduce(
        (acc, field) => {
            acc[field] = createFieldSchema(field);
            return acc;
        },
        {} as Record<DisplayField, z.ZodType<Partial<FieldSchemaValue>>>
    );

    return z.object(schemaObject);
};

const schema = createZodSchema(Object.values(DisplayField));

export type FormValues = z.infer<typeof schema>;

// TODO: use schema in resolver
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const mergeFormSchema = z.object({
    targetId: z.string(),
    guestProfileDetails: schema.optional(),
});
export type MergeFormSchema = z.infer<typeof mergeFormSchema>;

export const getKeyMergeUpdatesInput = (field: DisplayField) => {
    switch (field) {
        case DisplayField.LoyaltyMembership:
            return 'loyaltyMembershipId';
        default:
            return getKeyByDisplayField(field);
    }
};
export const transformFormValues = (formValues: FormValues) =>
    Object.keys(formValues).reduce(
        (acc, key) => {
            const field = key as DisplayField;
            const fieldValue = formValues[field];
            const keyName = getKeyMergeUpdatesInput(field);
            acc[keyName] = { value: fieldValue?.value ?? null };

            return acc;
        },
        {} as Record<string, FieldSchemaValue>
    );
